import { useState, useEffect, useRef } from "react";
import { db } from "../../firebase";
import {
  collection, addDoc, serverTimestamp,
  getDocs, query, where,
} from "firebase/firestore";

import { isMRFCategory } from "../../constants/enums/itemCategoryEnum";

export default function MRF() {
  const user      = JSON.parse(localStorage.getItem("user") || "{}");
  const companyId = user.company_id || "";

  const [activeTab, setActiveTab] = useState("material");

  // ══ TAB 1 STATE ══════════════════════════════════════════
  const [matItems, setMatItems] = useState([{
    row_id: Date.now(), item_name: "", item_id: "",
    qty: 1, unit: "", search: "", suggestions: [],
  }]);
  const [masterItems, setMasterItems] = useState([]);
  const [matLoading,  setMatLoading]  = useState(false);
  const [clients,      setClients]      = useState([]);
  const [sites,        setSites]        = useState([]);
  const [projects,     setProjects]     = useState([]);
  const [clientId,     setClientId]     = useState("");
  const [selectedSite, setSelectedSite] = useState(null);
  const [selectedProject, setSelectedProject] = useState(null);
  const topItemRef = useRef(null);

  // ══ TAB 2 STATE ══════════════════════════════════════════
  const [toolAssets,      setToolAssets]      = useState([]);
  const [toolSearch,      setToolSearch]      = useState("");
  const [toolSuggestions, setToolSuggestions] = useState([]);
  const [selectedTools,   setSelectedTools]   = useState([]);
  const [selectedToolSite, setSelectedToolSite] = useState(null);

  const [serviceTaskId, setServiceTaskId] = useState("");
  const [toolRemarks,   setToolRemarks]   = useState("");
  const [toolLoading,   setToolLoading]   = useState(false);

  // ══ ITEM REQUEST STATE ════════════════════════════════════
  const [showReqModal, setShowReqModal] = useState(false);
  const [reqRowIndex,  setReqRowIndex]  = useState(null);
  const [reqName,      setReqName]      = useState("");
  const [reqUnit,      setReqUnit]      = useState("");
  const [reqCategory,  setReqCategory]  = useState("");
  const [reqDesc,      setReqDesc]      = useState("");
  const [reqLoading,   setReqLoading]   = useState(false);
  const [reqError,     setReqError]     = useState("");
  const [reqSuccess,   setReqSuccess]   = useState(false);

  // ── FETCH CLIENTS + SITES ─────────────────────────────────
  useEffect(() => {
    if (!companyId) return;
    const fetch = async () => {
      const [cSnap, sSnap, pSnap] = await Promise.all([
        getDocs(query(collection(db, "clients"),  where("company_id", "==", companyId))),
        getDocs(query(collection(db, "sites"),    where("company_id", "==", companyId))),
        getDocs(query(collection(db, "projects"), where("company_id", "==", companyId), where("status", "==", "active"))),
      ]);
      setClients(cSnap.docs.map(d => ({ id: d.id, ...d.data() })));
      setSites(sSnap.docs.map(d => ({ id: d.id, ...d.data() })));
      setProjects(pSnap.docs.map(d => ({ id: d.id, ...d.data() })));
    };
    fetch();
  }, [companyId]);

  // ── FETCH MATERIAL ITEMS ──────────────────────────────────
  useEffect(() => {
    if (!companyId) return;
    const fetch = async () => {
      const snap = await getDocs(
        query(collection(db, "items"),
          where("company_id", "==", companyId),
          where("is_active",  "==", true))
      );
      const data = snap.docs.map(d => ({ id: d.id, ...d.data() }));
      setMasterItems(data.filter(item => isMRFCategory(item.category)));
    };
    fetch();
  }, [companyId]);

  // ── FETCH TOOL ASSETS ─────────────────────────────────────
  useEffect(() => {
    if (!companyId) return;
    const fetch = async () => {
      const snap = await getDocs(
        query(collection(db, "asset_master"),
          where("company_id", "==", companyId),
          where("status",     "==", "ACTIVE"))
      );
      setToolAssets(snap.docs.map(d => ({ id: d.id, ...d.data() })));
    };
    fetch();
  }, [companyId]);

  // ════════════════════════════════════════════════════════
  // TAB 1 — MATERIAL LOGIC
  // ════════════════════════════════════════════════════════

  const handleMatSearch = (index, value) => {
    const updated = [...matItems];
    updated[index].search  = value;
    updated[index].item_id = "";
    const filtered = masterItems.filter(i =>
      i.item_name.toLowerCase().includes(value.toLowerCase())
    );
    const uniqueMap = {};
    filtered.forEach(item => {
      const key = (item.item_name || "").toLowerCase().trim();
      if (!uniqueMap[key]) uniqueMap[key] = item;
    });
    updated[index].suggestions = Object.values(uniqueMap);
    const exactMatch = updated[index].suggestions.find(
      s => String(s.item_name || "").toLowerCase().trim() === String(value || "").toLowerCase().trim()
    );
    if (!exactMatch && value.trim().length >= 3) {
      updated[index].is_custom_item        = true;
      updated[index].inventory_linked      = false;
      updated[index].requires_master_review = true;
    } else {
      updated[index].is_custom_item        = false;
      updated[index].inventory_linked      = true;
      updated[index].requires_master_review = false;
    }
    setMatItems(updated);
  };

  const selectMatItem = (index, item) => {
    const selectedItemId = String(item.item_id || item.id || "").trim();
    const dup = matItems.findIndex((e, i) =>
      i !== index && String(e.item_id || "").trim() === selectedItemId
    );
    if (dup !== -1) { alert("This item is already added. Please update the quantity in the existing row."); return; }
    const updated = [...matItems];
    updated[index] = {
      ...updated[index],
      is_custom_item: false, inventory_linked: true, requires_master_review: false,
      item_id:   selectedItemId,
      item_code: item.item_code || "",
      item_name: item.item_name || "",
      category:  String(item.category || "").trim().toUpperCase(),
      procurement_type: String(item.procurement_type || "purchase").trim().toLowerCase(),
      unit:      item.unit || item.uom || "nos",
      search:    item.item_name || "",
      suggestions: [],
    };
    setMatItems(updated);
  };

  const updateMatItem = (index, field, value) => {
    const updated = [...matItems];
    updated[index][field] = value;
    setMatItems(updated);
  };

  const addMatItem = () => {
    setMatItems(prev => [
      { row_id: Date.now(), item_name: "", item_id: "", qty: 1, unit: "", search: "", suggestions: [] },
      ...prev,
    ]);
    setTimeout(() => topItemRef.current?.scrollIntoView({ behavior: "smooth", block: "start" }), 100);
  };

  const removeMatItem = (rowId) => {
    if (matItems.length === 1) return;
    setMatItems(prev => prev.filter(i => i.row_id !== rowId));
  };

  const handleSiteChange = (siteId) => {
    const site = sites.find(s => s.id === siteId);
    if (!site) {
      setSelectedSite(null);
      setClientId("");
      setSelectedProject(null);
      return;
    }
    const client = clients.find(c => c.id === site.client_id);
    setClientId(site.client_id || "");
    setSelectedSite({
      id: site.id, site_name: site.site_name,
      address:     site.address  || "",
      location:    site.location || site.location_link || "",
      client_name: client?.client_name || "",
    });

    // Auto-resolve Project for this site — technicians know the Site, not
    // the Project's internal naming, so Project is now derived FROM Site
    // (not the other way around). Auto-select only when exactly one active
    // project exists for this site; leave for manual pick via the (now
    // site-filtered) Project dropdown if there are several; clear it if
    // there are none — Project stays optional, site-level-only material
    // requests are valid.
    const matchingProjects = projects.filter(p => p.site_id === site.id);
    if (matchingProjects.length === 1) {
      const p = matchingProjects[0];
      setSelectedProject({ id: p.id, project_name: p.project_name, site_id: p.site_id, client_id: p.client_id });
    } else {
      setSelectedProject(null);
    }
  };

  // ══ ITEM REQUEST HANDLER ══════════════════════════════════
  const handleItemRequest = async () => {
    if (!reqName.trim()) { setReqError("Item name required"); return; }
    if (!reqUnit.trim()) { setReqError("Unit required");      return; }
    setReqLoading(true);
    setReqError("");
    try {
      // Check 1 — already in items master
      const existingSnap = await getDocs(query(
        collection(db, "items"),
        where("company_id",      "==", companyId),
        where("item_name_lower", "==", reqName.toLowerCase().trim())
      ));
      if (!existingSnap.empty) {
        setReqError(`"${reqName}" already exists in Item Master!`);
        setReqLoading(false);
        return;
      }
      // Check 2 — already pending request
      const pendingSnap = await getDocs(query(
        collection(db, "item_requests"),
        where("company_id",      "==", companyId),
        where("item_name_lower", "==", reqName.toLowerCase().trim()),
        where("status",          "==", "pending")
      ));
      if (!pendingSnap.empty) {
        setReqError("This item is already pending approval.");
        setReqLoading(false);
        return;
      }
      // Submit
      await addDoc(collection(db, "item_requests"), {
        item_name:         reqName.trim(),
        item_name_lower:   reqName.toLowerCase().trim(),
        unit:              reqUnit.trim(),
        category: ["MATERIAL","CONSUMABLE"].includes(
  reqCategory.trim().toUpperCase()
) ? reqCategory.trim().toUpperCase() : "MATERIAL",
        description:       reqDesc.trim(),
        requested_by_uid:  user.uid || user.id || "",
        requested_by_name: user.name || "",
        company_id:        companyId,
        status:            "pending",
        reject_reason:     "",
        approved_item_id:  null,
        created_at:        serverTimestamp(),
      });
      setReqSuccess(true);
      setTimeout(() => {
        setShowReqModal(false);
        setReqName(""); setReqUnit("");
        setReqCategory(""); setReqDesc("");
        setReqSuccess(false); setReqError("");
      }, 1500);
    } catch (err) {
      console.error("Item request error:", err);
      setReqError("Error submitting. Try again.");
    } finally {
      setReqLoading(false);
    }
  };

  const handleMatSubmit = async () => {
    try {
      setMatLoading(true);
      if (!selectedSite) { alert("Please select a site."); return; }
if (!clientId)     { alert("Client not found for selected site."); return; }
if (!selectedProject) {
  const confirm = window.confirm("No project selected. Material will be tracked at site level only.\n\nContinue?");
  if (!confirm) return;
}
      for (let i of matItems) {
        if (!i.item_id) { i.is_custom_item = true; i.requires_master_review = true; i.inventory_linked = false; }
        if (i.is_custom_item && !String(i.item_name || "").trim()) { alert("Please enter a name for the custom item."); return; }
        if (!String(i.unit || "").trim()) { alert(`Unit missing for item: "${i.item_name || "Unknown"}". Please fill the unit.`); return; }
        if (i.item_id && !i.is_custom_item) {
          if (!isMRFCategory(String(i.category || "").trim().toUpperCase())) {
            alert(`"${i.item_name}" is a ${i.category}. Please use the Tools tab for this item.`); return;
          }
        }
      }
      const finalItems = matItems.map(i => ({
        item_id:   i.item_id   || "", item_name: i.item_name,
        item_code: i.item_code || "",
        category:  String(i.category || "").trim().toUpperCase(),
        procurement_type: String(i.procurement_type || "purchase").trim().toLowerCase(),
        qty: Number(i.qty), unit: i.unit || "nos",
        is_custom_item:        Boolean(i.is_custom_item),
        requires_master_review: Boolean(i.is_custom_item),
        inventory_linked:      !i.is_custom_item,
        remarks: i.remarks || "",
      }));
      // ✅ FIX: addDoc ka ref capture karo — ID custom items ke liye chahiye
      const mrfRef = await addDoc(collection(db, "material_requests"), {
        company_id:         companyId,
        requested_by:       user.uid || user.id || "",
        requested_by_name:  user.name || "",
        project_id:         selectedProject?.id   || "",
        project_name:       selectedProject?.project_name || "",
        client_id:          clientId,
        client_name:        clients.find(c => c.id === clientId)?.client_name || "",
        site_id:            selectedSite.id   || "",
        site_name:          selectedSite.site_name || "",
        status:             "pending",
        is_processed:       false,
        created_at:         serverTimestamp(),
        items:              finalItems,
      });

      // ── Custom items → auto item_requests mein save karo ──
      const customItems = finalItems
        .map((item, index) => ({ ...item, _index: index }))  // ✅ index preserve
        .filter(i => i.is_custom_item && i.item_name?.trim());

      for (const ci of customItems) {
        const pendingSnap = await getDocs(query(
          collection(db, "item_requests"),
          where("company_id",      "==", companyId),
          where("item_name_lower", "==", (ci.item_name || "").toLowerCase().trim()),
          where("status",          "==", "pending")
        ));
        if (pendingSnap.empty) {
          await addDoc(collection(db, "item_requests"), {
            item_name:         ci.item_name.trim(),
            item_name_lower:   ci.item_name.toLowerCase().trim(),
            unit:              ci.unit || "",
            // ✅ category uppercase karo, General → MATERIAL
category: ["MATERIAL","CONSUMABLE"].includes(
  String(ci.category || "").toUpperCase()
) ? String(ci.category).toUpperCase() : "MATERIAL",
            description:       ci.remarks  || "",
            requested_by_uid:  user.uid || user.id || "",
            requested_by_name: user.name || "",
            company_id:        companyId,
            status:            "pending",
            reject_reason:     "",
            approved_item_id:  null,
            source:            "mrf_custom_item",
            source_mrf_id:     mrfRef.id,   // ✅ NEW — MRF ka Firestore ID
            item_index:        ci._index,    // ✅ NEW — items array mein position
            created_at:        serverTimestamp(),
          });
        }
      }

      alert("Material Request submitted successfully.");
      setMatItems([{ row_id: Date.now(), item_name: "", item_id: "", qty: 1, unit: "", search: "", suggestions: [] }]);
      setClientId(""); setSelectedSite(null); setSelectedProject(null);
    } catch (err) {
      console.error("MRF submit error:", err);
      alert("Error creating MRF");
    } finally { setMatLoading(false); }
  };

  // ════════════════════════════════════════════════════════
  // TAB 2 — TOOL LOGIC
  // ════════════════════════════════════════════════════════

  const handleToolSearch = (value) => {
    setToolSearch(value);
    if (!value.trim()) { setToolSuggestions([]); return; }
    const lower = value.toLowerCase();
    const alreadySelected = new Set(selectedTools.map(t => t.id));
    setToolSuggestions(
      toolAssets.filter(a =>
        !alreadySelected.has(a.id) && (
          (a.asset_name     || "").toLowerCase().includes(lower) ||
          (a.asset_code     || "").toLowerCase().includes(lower) ||
          (a.asset_category || "").toLowerCase().includes(lower)
        )
      ).slice(0, 15)
    );
  };

  const addToolToCart = (asset) => {
    if (selectedTools.find(t => t.id === asset.id)) return;
    setSelectedTools(prev => [...prev, asset]);
    setToolSearch("");
    setToolSuggestions([]);
  };

  const removeToolFromCart = (assetId) => {
    setSelectedTools(prev => prev.filter(t => t.id !== assetId));
  };

  // ── REQUEST TOOLS ─────────────────────────────────────────
  const handleToolSubmit = async () => {
    if (selectedTools.length === 0) { alert("Add at least one tool"); return; }
    try {
      setToolLoading(true);

      const toolItems = selectedTools.map(asset => ({
        item_id:             asset.id,
        asset_id:            asset.asset_id || asset.id,
        item_name:           asset.asset_name,
        item_code:           asset.asset_code || asset.serial_no || "",
        category:            "TOOLS",
        unit:                "nos",
        qty:                 1,
        approved_qty:        1,
        issued_qty:          0,
        remaining_qty:       1,
        is_tool:             true,
        is_custom_item:      false,
        inventory_linked:    false,
        asset_category:      asset.asset_category      || "",
        current_holder_name: asset.current_holder_name || "Store",
        current_holder_id:   asset.current_holder_id   || "MAIN_STORE",
        current_holder_type: asset.current_holder_type || "STORE",
        item_status:         "pending",
      }));

      await addDoc(collection(db, "material_requests"), {
        company_id:        companyId,
        request_type:      "tool",
        requested_by:      user.uid || user.id || "",
        requested_by_name: user.name || "",
        // Receiver = logged-in user (technician requests for themselves)
        receiver_id:       user.uid || user.id || "",
        receiver_name:     user.name || "",
        receiver_role:     user.role || "technician",
        receiver_type:     "user",
        // Site where tool is needed (optional)
        site_id:           selectedToolSite?.id        || "",
        site_name:         selectedToolSite?.site_name || "",
        service_task_id:   serviceTaskId || "",
        remarks:           toolRemarks   || "",
        status:            "pending",
        is_processed:      false,
        created_at:        serverTimestamp(),
        items:             toolItems,
      });

      alert(`Tool request submitted. ${selectedTools.length} tool(s) requested. Store Manager will approve and issue.`);

      setSelectedTools([]);
      setSelectedToolSite(null);
      setServiceTaskId("");
      setToolRemarks("");
    } catch (err) {
      console.error("Tool request error:", err);
      alert(err.message || "Tool request failed");
    } finally {
      setToolLoading(false);
    }
  };

  // ════════════════════════════════════════════════════════
  // UI
  // ════════════════════════════════════════════════════════

  return (
    <div className="p-4 pb-24 bg-gray-50 min-h-screen">
      <h2 className="text-lg font-bold mb-4 text-slate-800">Create Request</h2>

      {/* TABS */}
      <div className="flex gap-2 mb-5">
        <button onClick={() => setActiveTab("material")}
          className={`flex-1 py-3 rounded-2xl text-sm font-semibold transition ${
            activeTab === "material"
              ? "bg-gradient-to-r from-cyan-500 to-blue-600 text-white shadow-md"
              : "bg-white text-slate-500 border border-slate-200"
          }`}>
          📦 Materials
        </button>
        <button onClick={() => setActiveTab("tool")}
          className={`flex-1 py-3 rounded-2xl text-sm font-semibold transition ${
            activeTab === "tool"
              ? "bg-gradient-to-r from-violet-500 to-indigo-600 text-white shadow-md"
              : "bg-white text-slate-500 border border-slate-200"
          }`}>
          🔧 Tools / Assets
        </button>
      </div>

      {/* ══ TAB 1 — MATERIALS ════════════════════════════════ */}
      {activeTab === "material" && (
        <div className="bg-white p-4 rounded-2xl shadow-sm border border-slate-100 space-y-4">

          {/* Site select — required, comes FIRST: technicians recognize the
              physical site, not the Project's internal naming convention. */}
          <div>
  <label className="text-xs font-semibold text-slate-500 uppercase tracking-wide mb-1 block">
    Site * <span className="font-normal normal-case text-slate-400">(required)</span>
  </label>
  <select onChange={e => handleSiteChange(e.target.value)}
    value={selectedSite?.id || ""}
    className="w-full border border-slate-200 p-3 rounded-xl text-sm bg-slate-50 outline-none">
    <option value="">Select Site *</option>
    {sites.map(s => <option key={s.id} value={s.id}>{s.site_name}</option>)}
  </select>
</div>

          {/* Project select — auto-derived from the selected Site (auto-picked
              if only one project exists for that site, manual choice if
              several, stays optional/blank if none). Disabled until a Site
              is chosen, and options are filtered to that Site's projects
              only — never the full company project list. */}
          <div>
            <label className="text-xs font-semibold text-slate-500 uppercase tracking-wide mb-1 block">
              Project <span className="font-normal normal-case text-slate-400">(optional)</span>
            </label>
            <select
              value={selectedProject?.id || ""}
              onChange={e => {
                const p = projects.find(p => p.id === e.target.value);
                setSelectedProject(p ? { id: p.id, project_name: p.project_name, site_id: p.site_id, client_id: p.client_id } : null);
              }}
              disabled={!selectedSite}
              className="w-full border border-slate-200 p-3 rounded-xl text-sm bg-slate-50 outline-none focus:ring-2 focus:ring-cyan-400 disabled:opacity-50 disabled:cursor-not-allowed">
              <option value="">{selectedSite ? "Select Project (optional)" : "Select a site first"}</option>
              {selectedSite && projects
                .filter(p => p.site_id === selectedSite.id)
                .map(p => (
                  <option key={p.id} value={p.id}>{p.project_name}</option>
                ))}
            </select>
          </div>

          <input value={selectedSite?.client_name || ""} placeholder="Client (auto)"
            readOnly className="w-full border border-slate-200 p-3 rounded-xl text-sm bg-slate-100" />

          <div ref={topItemRef} className="space-y-3">
            {matItems.map((item, i) => (
              <div key={item.row_id}
                ref={i === 0 ? topItemRef : null}
                className="bg-slate-50 border border-slate-200 p-4 rounded-2xl space-y-3 relative">
                <div className="flex items-center justify-between">
                  <span className="text-sm font-semibold text-slate-700">Item #{matItems.length - i}</span>
                  <button onClick={() => removeMatItem(item.row_id)} disabled={matItems.length === 1}
                    className="px-3 py-1 rounded-full bg-red-50 text-red-500 text-xs font-semibold border border-red-100 disabled:opacity-40">
                    🗑 Remove
                  </button>
                </div>

                <div className="relative">
                  <input placeholder="Search Material / Consumable..."
                    value={item.search}
                    onChange={e => {
                      updateMatItem(i, "search",     e.target.value);
                      updateMatItem(i, "item_name",  e.target.value);
                      handleMatSearch(i, e.target.value);
                    }}
                    className="w-full border border-slate-200 p-3 rounded-xl text-sm bg-white outline-none focus:ring-2 focus:ring-cyan-400"
                  />
                  {item.suggestions?.length > 0 && (
                    <div className="absolute left-0 top-full mt-1 bg-white border border-slate-200 w-full rounded-xl shadow-xl overflow-y-auto"
                      style={{ zIndex: 9999, maxHeight: "200px" }}>
                      {item.suggestions.map((s, idx) => (
                        <div key={idx} onClick={() => selectMatItem(i, s)}
                          className="px-4 py-3 border-b border-slate-100 cursor-pointer hover:bg-cyan-50 text-sm last:border-0">
                          <span className="font-medium">{s.item_name}</span>
                          {s.unit && <span className="ml-2 text-xs text-slate-400">{s.unit}</span>}
                        </div>
                      ))}
                    </div>
                  )}
                </div>

                {item.is_custom_item && !item.item_id && (
                  <div className="space-y-2">
                    <div className="text-amber-600 text-xs font-semibold bg-amber-50 px-3 py-1.5 rounded-lg">
                      ⚠ Item not found in master
                    </div>
                    <button
                      type="button"
                      onClick={() => {
                        setReqName(item.search || "");
                        setReqUnit("");
                        setReqCategory("");
                        setReqDesc("");
                        setReqError("");
                        setReqSuccess(false);
                        setReqRowIndex(i);
                        setShowReqModal(true);
                      }}
                      className="w-full text-sm text-blue-600 border border-blue-200 bg-blue-50 hover:bg-blue-100 py-2 rounded-xl font-semibold transition"
                    >
                      + Request New Item (Pending Manager Approval)
                    </button>
                  </div>
                )}

                <input type="number" value={item.qty} min={1}
                  onChange={e => updateMatItem(i, "qty", e.target.value)}
                  placeholder="Quantity"
                  className="w-full border border-slate-200 p-3 rounded-xl text-sm bg-white outline-none focus:ring-2 focus:ring-cyan-400" />

                {item.is_custom_item && (
                  <textarea placeholder="Specification / remarks"
                    value={item.remarks || ""}
                    onChange={e => updateMatItem(i, "remarks", e.target.value)}
                    className="w-full border border-slate-200 p-3 rounded-xl text-sm bg-white resize-none" rows={2} />
                )}

                <input value={item.unit || ""} readOnly={!item.is_custom_item}
                  onChange={e => { if (item.is_custom_item) updateMatItem(i, "unit", e.target.value); }}
                  placeholder="Unit"
                  className={`w-full border border-slate-200 p-3 rounded-xl text-sm ${item.is_custom_item ? "bg-white" : "bg-slate-100"}`} />

                {i === 0 && (
                  <button onClick={addMatItem}
                    className="w-full border-2 border-dashed border-cyan-300 text-cyan-600 hover:bg-cyan-50 py-2.5 rounded-xl text-sm font-semibold transition">
                    + Add Item
                  </button>
                )}
              </div>
            ))}
          </div>

          <button onClick={handleMatSubmit} disabled={matLoading}
            className="w-full bg-blue-600 hover:bg-blue-700 text-white py-3.5 rounded-xl shadow font-semibold text-sm transition disabled:opacity-50">
            {matLoading ? "Saving..." : "Submit Material Request"}
          </button>
        </div>
      )}

      {/* ══ TAB 2 — TOOLS ════════════════════════════════════ */}
      {activeTab === "tool" && (
        <div className="bg-white p-4 rounded-2xl shadow-sm border border-slate-100 space-y-4">

          {/* TOOL SEARCH */}
          <div>
            <label className="text-xs font-semibold text-slate-500 uppercase tracking-wide mb-2 block">
              Add Tools / Assets
            </label>
            <div className="relative">
              <input
                placeholder="Search tool by name or code..."
                value={toolSearch}
                onChange={e => handleToolSearch(e.target.value)}
                autoComplete="off" autoCorrect="off" spellCheck="false"
                className="w-full border border-slate-200 p-3 rounded-xl text-sm bg-slate-50 outline-none focus:ring-2 focus:ring-violet-400"
              />
              {toolSuggestions.length > 0 && (
                <div className="absolute left-0 top-full mt-1 bg-white border border-slate-200 w-full rounded-xl shadow-xl overflow-y-auto"
                  style={{ zIndex: 9999, maxHeight: "220px" }}>
                  {toolSuggestions.map(a => {
                    const inStore = a.current_holder_type === "STORE" || !a.current_holder_name;
                    return (
                      <div key={a.id} onClick={() => addToolToCart(a)}
                        className="flex items-center justify-between px-4 py-3 border-b border-slate-100 cursor-pointer hover:bg-violet-50 active:bg-violet-100 last:border-0">
                        <div>
                          <div className="font-semibold text-sm text-slate-800">{a.asset_name}</div>
                          <div className="flex gap-2 mt-0.5 flex-wrap">
                            {a.asset_code && <span className="text-xs text-slate-400">{a.asset_code}</span>}
                            {a.asset_category && (
                              <span className="text-[10px] px-2 py-0.5 rounded-full bg-violet-100 text-violet-700 font-semibold uppercase">
                                {a.asset_category}
                              </span>
                            )}
                            {inStore ? (
                              <span className="text-[10px] px-2 py-0.5 rounded-full bg-green-100 text-green-700 font-semibold">✓ In Store</span>
                            ) : (
                              <span className="text-[10px] px-2 py-0.5 rounded-full bg-amber-100 text-amber-700 font-semibold">
                                With: {a.current_holder_name}
                              </span>
                            )}
                          </div>
                        </div>
                        <span className="text-violet-400 text-lg shrink-0">+</span>
                      </div>
                    );
                  })}
                </div>
              )}
            </div>
            {toolAssets.length === 0 && (
              <p className="text-xs text-slate-400 mt-2 text-center py-2">No assets found. Add assets via Asset Master first.</p>
            )}
          </div>

          {/* CART */}
          {selectedTools.length > 0 && (
            <div>
              <label className="text-xs font-semibold text-slate-500 uppercase tracking-wide mb-2 block">
                Selected Tools ({selectedTools.length})
              </label>
              <div className="space-y-2">
                {selectedTools.map(asset => (
                  <div key={asset.id}
                    className="flex items-center justify-between px-4 py-3 rounded-xl bg-violet-50 border border-violet-200">
                    <div>
                      <div className="text-sm font-bold text-violet-900">{asset.asset_name}</div>
                      <div className="flex gap-2 mt-0.5 flex-wrap">
                        {asset.asset_code && <span className="text-xs text-violet-500">{asset.asset_code}</span>}
                        {asset.asset_category && (
                          <span className="text-[10px] px-2 py-0.5 rounded-full bg-violet-200 text-violet-800 font-semibold uppercase">
                            {asset.asset_category}
                          </span>
                        )}
                        {(asset.current_holder_type === "STORE" || !asset.current_holder_name) ? (
                          <span className="text-[10px] px-2 py-0.5 rounded-full bg-green-100 text-green-700 font-semibold">✓ In Store</span>
                        ) : (
                          <span className="text-[10px] px-2 py-0.5 rounded-full bg-amber-100 text-amber-700 font-semibold">
                            Transfer from: {asset.current_holder_name}
                          </span>
                        )}
                      </div>
                    </div>
                    <button onClick={() => removeToolFromCart(asset.id)}
                      className="text-violet-400 hover:text-red-500 text-lg font-bold px-2 transition">✕</button>
                  </div>
                ))}
              </div>
            </div>
          )}

          {/* SITE */}
          <div>
            <label className="text-xs font-semibold text-slate-500 uppercase tracking-wide mb-2 block">
              Site <span className="font-normal normal-case text-slate-400">(where is tool needed)</span>
            </label>
            <select
              value={selectedToolSite?.id || ""}
              onChange={e => {
                const site = sites.find(s => s.id === e.target.value);
                setSelectedToolSite(site ? { id: site.id, site_name: site.site_name } : null);
              }}
              className="w-full border border-slate-200 p-3 rounded-xl text-sm bg-slate-50 outline-none focus:ring-2 focus:ring-violet-400">
              <option value="">Select Site (optional)</option>
              {sites.map(s => <option key={s.id} value={s.id}>{s.site_name}</option>)}
            </select>
          </div>

          {/* SERVICE JOB */}
          <div>
            <label className="text-xs font-semibold text-slate-500 uppercase tracking-wide mb-2 block">
              Service Job ID <span className="font-normal normal-case text-slate-400">(optional)</span>
            </label>
            <input placeholder="Link to a service task..."
              value={serviceTaskId} onChange={e => setServiceTaskId(e.target.value)}
              className="w-full border border-slate-200 p-3 rounded-xl text-sm bg-slate-50 outline-none focus:ring-2 focus:ring-violet-400" />
          </div>

          {/* REMARKS */}
          <div>
            <label className="text-xs font-semibold text-slate-500 uppercase tracking-wide mb-2 block">
              Remarks <span className="font-normal normal-case text-slate-400">(optional)</span>
            </label>
            <textarea rows={2} placeholder="Purpose, job details..."
              value={toolRemarks} onChange={e => setToolRemarks(e.target.value)}
              className="w-full border border-slate-200 p-3 rounded-xl text-sm bg-slate-50 resize-none outline-none focus:ring-2 focus:ring-violet-400" />
          </div>

        

          {/* SUBMIT */}
          <button onClick={handleToolSubmit}
            disabled={toolLoading || selectedTools.length === 0}
            className="w-full py-3.5 rounded-2xl bg-gradient-to-r from-violet-500 to-indigo-600 text-white font-semibold text-sm shadow-md hover:opacity-95 transition disabled:opacity-40 disabled:cursor-not-allowed">
            {toolLoading
              ? "Submitting Request..."
              : `Request ${selectedTools.length > 0 ? `${selectedTools.length} ` : ""}Tool${selectedTools.length !== 1 ? "s" : ""} →`}
          </button>

          <p className="text-[11px] text-slate-400 text-center pb-2">
  Request will be reviewed by Store Manager for approval and issue.
</p>
        </div>
      )}
    {/* ══ ITEM REQUEST MODAL ═══════════════════════════════ */}
      {showReqModal && (
        <div className="fixed inset-0 bg-black/50 flex items-end justify-center z-50 p-4">
          <div className="bg-white rounded-3xl w-full max-w-sm shadow-2xl p-5">

            <div className="flex justify-between items-center mb-3">
              <h3 className="font-bold text-slate-800 text-base">Request New Item</h3>
              <button onClick={() => setShowReqModal(false)}
                className="text-slate-400 text-xl font-bold leading-none">✕</button>
            </div>

            <p className="text-xs text-slate-400 mb-4 bg-slate-50 px-3 py-2 rounded-xl">
  Owner or Manager will review and approve this item. Once approved, it will be added to the Item Master and available in future requests.
</p>

            <div className="space-y-3">

              <div>
                <label className="text-xs font-semibold text-slate-500 mb-1 block">
                  Item Name *
                </label>
                <input
                  value={reqName}
                  onChange={e => setReqName(e.target.value)}
                  placeholder="e.g. Copper Pipe 3/4 inch"
                  className="w-full border border-slate-200 rounded-xl px-3 py-2.5 text-sm outline-none focus:ring-2 focus:ring-blue-300"
                />
              </div>

              <div className="grid grid-cols-2 gap-2">
                <div>
                  <label className="text-xs font-semibold text-slate-500 mb-1 block">Unit *</label>
                  <input
                    value={reqUnit}
                    onChange={e => setReqUnit(e.target.value)}
                    placeholder="Mtr / Nos / Kg"
                    className="w-full border border-slate-200 rounded-xl px-3 py-2.5 text-sm outline-none focus:ring-2 focus:ring-blue-300"
                  />
                </div>
                <div>
                  <label className="text-xs font-semibold text-slate-500 mb-1 block">Category</label>
                  <select
                    value={reqCategory || "MATERIAL"}
                    onChange={e => setReqCategory(e.target.value)}
                    className="w-full border border-slate-200 rounded-xl px-3 py-2.5 text-sm outline-none focus:ring-2 focus:ring-blue-300 bg-white"
                  >
                    <option value="MATERIAL">Material</option>
                    <option value="CONSUMABLE">Consumable</option>
                  </select>
                </div>
              </div>

              <div>
                <label className="text-xs font-semibold text-slate-500 mb-1 block">
                  Description <span className="font-normal text-slate-400">(optional)</span>
                </label>
                <textarea
                  value={reqDesc}
                  onChange={e => setReqDesc(e.target.value)}
                  placeholder="Specification, size, brand..."
                  rows={2}
                  className="w-full border border-slate-200 rounded-xl px-3 py-2.5 text-sm resize-none outline-none focus:ring-2 focus:ring-blue-300"
                />
              </div>

              {reqError && (
                <p className="text-red-500 text-xs bg-red-50 px-3 py-2 rounded-xl">❌ {reqError}</p>
              )}
              {reqSuccess && (
                <p className="text-green-600 text-xs bg-green-50 px-3 py-2 rounded-xl">
                  ✅ Request submitted! Owner or Manager will approve.
                </p>
              )}

              <div className="flex gap-2 pt-1">
                <button
                  onClick={() => setShowReqModal(false)}
                  className="flex-1 border border-slate-200 text-slate-500 rounded-xl py-2.5 text-sm font-medium"
                >Cancel</button>
                <button
                  onClick={handleItemRequest}
                  disabled={reqLoading || reqSuccess}
                  className="flex-[2] bg-blue-600 hover:bg-blue-700 text-white rounded-xl py-2.5 text-sm font-semibold transition disabled:opacity-50"
                >
                  {reqLoading ? "Submitting..." : "Submit Request"}
                </button>
              </div>

            </div>
          </div>
        </div>
      )}

    </div>
  );
}
